// src/pages/Consignacion/Tabs/TabReclamos.js
//
// Pendiente de entrega por orden de compra.
//
// La solapa de Reposición dice qué falta comprar. Esta dice qué falta que
// llegue: de las órdenes ya aprobadas, qué líneas siguen abiertas contra el
// almacén de consignación y hace cuánto están demoradas.
//
// Por qué existe aparte: el reclamo es contra el proveedor y se hace por
// documento, no por artículo. Un artículo puede estar pendiente en dos órdenes
// distintas, y cada una tiene su fecha comprometida y su responsable.
//
// El correo sale por el mismo endpoint que el Asistente de Reclamos de
// Informes (casilla de Compras + Centro de Notificaciones): duplicar el envío
// habría significado dos plantillas y dos logs para el mismo trámite.
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import * as XLSX from 'xlsx';
import api from '../../../services/api';
import { useToast } from '../../../components/Toast/ToastProvider';
import { formatMoney, formatNumber, formatFecha } from '../../../utils/formato';

const EMPRESA = process.env.REACT_APP_EMPRESA_DEFAULT || 'CIMSA';

// Cómo se muestra cada estado de entrega. El texto acompaña al color: el
// estado es el dato que decide si se reclama o no, y no puede depender de
// distinguir rojo de amarillo.
const ESTADOS = {
  vencido:   { titulo: 'Vencida',    clase: 'text-bg-danger',  icono: 'bi-exclamation-octagon' },
  porVencer: { titulo: 'Por vencer', clase: 'text-bg-warning',  icono: 'bi-hourglass-split' },
  enFecha:   { titulo: 'En fecha',   clase: 'text-bg-success',  icono: 'bi-check-circle' },
  sinFecha:  { titulo: 'Sin fecha',  clase: 'text-bg-secondary', icono: 'bi-question-circle' }
};

/** "hace 12 días" / "en 9 días": el número solo no dice de qué lado está. */
const textoDias = (dias) => {
  if (dias === null || dias === undefined) return 'sin fecha comprometida';
  if (dias < 0)  return `demorada ${Math.abs(dias)} día(s)`;
  if (dias === 0) return 'vence hoy';
  return `faltan ${dias} día(s)`;
};

const TabReclamos = () => {
  const { showToast } = useToast();

  const [datos, setDatos] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(null);

  const [busqueda, setBusqueda] = useState('');
  const [filtroEstado, setFiltroEstado] = useState('TODOS');
  const [expandidas, setExpandidas] = useState([]);

  // Modal de correo
  const [ocReclamo, setOcReclamo] = useState(null);
  const [mailPara, setMailPara] = useState('');
  const [mailAsunto, setMailAsunto] = useState('');
  const [mailCuerpo, setMailCuerpo] = useState('');
  const [enviando, setEnviando] = useState(false);

  const cargar = useCallback(async (signal) => {
    setCargando(true);
    setError(null);
    try {
      const { data } = await api.get('/consignacion/reclamos', { signal });
      setDatos(data);
    } catch (err) {
      if (err.code === 'ERR_CANCELED' || err.name === 'CanceledError') return;
      setError(err.response?.data?.error || err.message);
    } finally {
      if (!signal?.aborted) setCargando(false);
    }
  }, []);

  useEffect(() => {
    const ctrl = new AbortController();
    cargar(ctrl.signal);
    return () => ctrl.abort();
  }, [cargar]);

  const ordenes = useMemo(() => {
    const todas = datos?.ordenes || [];
    const termino = busqueda.trim().toLowerCase();
    return todas.filter(oc => {
      if (filtroEstado !== 'TODOS' && oc.estado !== filtroEstado) return false;
      if (!termino) return true;
      return String(oc.docNum).includes(termino)
        || oc.cardName.toLowerCase().includes(termino)
        || oc.cardCode.toLowerCase().includes(termino)
        || oc.lineas.some(l =>
            l.codigo.toLowerCase().includes(termino) ||
            l.descripcion.toLowerCase().includes(termino));
    });
  }, [datos, busqueda, filtroEstado]);

  const alternar = (docEntry) => setExpandidas(prev =>
    prev.includes(docEntry) ? prev.filter(x => x !== docEntry) : [...prev, docEntry]);

  // ── Planilla de reclamos ────────────────────────────────────────────────
  // Una fila por línea pendiente, que es la unidad del reclamo. El resumen por
  // orden va en su propia hoja: sirve para el seguimiento, no para reclamar.
  const descargarPlanilla = () => {
    if (!ordenes.length) {
      showToast('No hay órdenes pendientes para exportar.', 'warning');
      return;
    }

    const filas = [];
    ordenes.forEach(oc => oc.lineas.forEach(l => filas.push({
      'OC': oc.docNum,
      'Fecha OC': formatFecha(oc.fecha),
      'Proveedor': oc.cardName,
      'Código proveedor': oc.cardCode,
      'Email proveedor': oc.email,
      'Comprador': oc.comprador,
      'Código artículo': l.codigo,
      'Descripción': l.descripcion,
      'Cant. pedida': l.pedido,
      'Cant. recibida': l.recibido,
      'Cant. pendiente': l.pendiente,
      'Precio unitario': l.precio,
      'Importe pendiente': l.importePendiente,
      'Fecha de entrega': l.fechaEntrega ? formatFecha(l.fechaEntrega) : '',
      'Días de atraso': l.dias === null ? '' : (l.dias < 0 ? Math.abs(l.dias) : 0),
      'Estado': ESTADOS[l.estado]?.titulo || l.estado,
      // El stock es lo que le da prioridad al reclamo: la misma demora no
      // pesa igual sobre un artículo con stock que sobre uno en cero.
      'Stock actual': l.stock ?? '',
      'Stock mínimo': l.stockMinimo ?? '',
      'En quiebre': l.enQuiebre ? 'SÍ' : ''
    })));

    const wb = XLSX.utils.book_new();

    const hoja = XLSX.utils.json_to_sheet(filas);
    hoja['!cols'] = [
      { wch: 9 }, { wch: 12 }, { wch: 32 }, { wch: 16 }, { wch: 28 }, { wch: 20 },
      { wch: 16 }, { wch: 52 }, { wch: 12 }, { wch: 13 }, { wch: 14 },
      { wch: 15 }, { wch: 17 }, { wch: 15 }, { wch: 13 }, { wch: 12 },
      { wch: 12 }, { wch: 12 }, { wch: 10 }
    ];
    hoja['!autofilter'] = { ref: XLSX.utils.encode_range({
      s: { r: 0, c: 0 }, e: { r: filas.length, c: 18 }
    }) };
    XLSX.utils.book_append_sheet(wb, hoja, 'Reclamos');

    const resumen = XLSX.utils.json_to_sheet(ordenes.map(oc => ({
      'OC': oc.docNum,
      'Proveedor': oc.cardName,
      'Email': oc.email,
      'Comprador': oc.comprador,
      'Fecha OC': formatFecha(oc.fecha),
      'Entrega comprometida': oc.fechaEntrega ? formatFecha(oc.fechaEntrega) : '',
      'Líneas pendientes': oc.lineas.length,
      'Unidades pendientes': oc.unidadesPendientes,
      'Importe pendiente': oc.importePendiente,
      'Estado': ESTADOS[oc.estado]?.titulo || oc.estado,
      'Situación': textoDias(oc.dias)
    })));
    resumen['!cols'] = [
      { wch: 9 }, { wch: 32 }, { wch: 28 }, { wch: 20 }, { wch: 12 },
      { wch: 20 }, { wch: 17 }, { wch: 19 }, { wch: 18 }, { wch: 12 }, { wch: 24 }
    ];
    XLSX.utils.book_append_sheet(wb, resumen, 'Resumen por OC');

    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet([
      ['Reclamos de consignación — pendiente de entrega'],
      [`Generada el ${new Date().toLocaleDateString('es-AR')}`],
      [],
      ['QUÉ CONTIENE'],
      [`Las líneas de órdenes de compra abiertas contra el almacén ${datos?.almacen || 'CONSIG'}`],
      ['que el proveedor todavía no entregó. Una fila por línea: es la unidad del reclamo.'],
      [],
      ['DE DÓNDE SALE CADA DATO'],
      ['Cant. pendiente', 'RemainingOpenQuantity de la línea de la OC en SAP'],
      ['Cant. recibida', 'lo pedido menos lo que sigue abierto (entradas parciales)'],
      ['Fecha de entrega', 'fecha de la línea; si no tiene, la del documento'],
      ['Días de atraso', 'días vencidos respecto de esa fecha; 0 si todavía está en plazo'],
      ['Stock actual', 'ficha del almacén de consignación'],
      [],
      ['ESTADOS'],
      ['Vencida', 'la fecha comprometida ya pasó'],
      ['Por vencer', `vence dentro de los próximos ${datos?.diasAviso ?? 3} días`],
      ['En fecha', 'todavía está en plazo'],
      ['Sin fecha', 'la OC no tiene fecha de entrega cargada — no está en plazo, no se sabe'],
      [],
      ['La planilla respeta los filtros aplicados en pantalla.']
    ]), 'Instrucciones');

    XLSX.writeFile(wb, `Reclamos de consignacion ${new Date().toISOString().slice(0, 10)}.xlsx`);
    showToast('Planilla descargada.', 'success');
  };

  // ── Correo de reclamo ───────────────────────────────────────────────────
  const abrirReclamo = (oc) => {
    const vencida = oc.dias !== null && oc.dias < 0;
    const fecha = oc.fechaEntrega ? formatFecha(oc.fechaEntrega) : 'sin fecha';

    let cuerpo = 'Estimado Proveedor,\nA quien corresponda:\n\n';
    cuerpo += vencida
      ? 'Nos comunicamos desde el Departamento de Compras para reclamar la entrega de la siguiente Orden de Compra, cuyo plazo se encuentra vencido:\n\n'
      : 'Nos comunicamos desde el Departamento de Compras para consultar la fecha de entrega de la siguiente Orden de Compra pendiente:\n\n';
    cuerpo += `• OC N° ${oc.docNum} - Fecha de entrega pactada: ${fecha}\n`;
    oc.lineas.forEach(l => {
      cuerpo += `    - ${l.codigo} ${l.descripcion} (${formatNumber(l.pendiente)} un.)\n`;
    });
    cuerpo += '\nNecesitamos que confirmen por este medio la nueva fecha de entrega.\n\n';
    cuerpo += 'Saludos cordiales,\n\nDepartamento de Compras\nGrupo Codylsa';

    setOcReclamo(oc);
    setMailPara(oc.email || '');
    setMailAsunto(`${vencida ? 'RECLAMO' : 'SEGUIMIENTO'} ENTREGA OC ${oc.docNum} - GRUPO CODYLSA`);
    setMailCuerpo(cuerpo);
  };

  const enviarReclamo = async () => {
    if (!mailPara || !mailPara.includes('@')) {
      showToast('Ingresá un email de destino válido.', 'warning');
      return;
    }
    setEnviando(true);
    try {
      const { data } = await api.post('/compras/seguimiento-oc/enviar-reclamo', {
        emailTo: mailPara,
        subject: mailAsunto,
        body: mailCuerpo,
        docNum: ocReclamo.docNum,
        docEntry: ocReclamo.docEntry,
        proveedor: ocReclamo.cardName,
        importe: ocReclamo.importePendiente,
        fecha: ocReclamo.fechaEntrega,
        empresa: EMPRESA,
        emailComprador: ocReclamo.emailComprador || ''
      });
      // El backend responde 200 con ok:false cuando el envío se rechaza: es el
      // resultado, no una falla del sistema.
      if (!data.ok) {
        showToast(data.error || 'No se pudo enviar el correo.', 'danger', 8000);
        return;
      }
      setOcReclamo(null);
      showToast(
        data.desviado
          ? `Enviado a la casilla de pruebas (el destinatario real era ${mailPara}).`
          : `Reclamo enviado a ${mailPara}`,
        data.desviado ? 'warning' : 'success', data.desviado ? 9000 : 5000);
    } catch (err) {
      showToast(err.response?.data?.error || 'Error al enviar el reclamo.', 'danger');
    } finally {
      setEnviando(false);
    }
  };

  // ── Render ──────────────────────────────────────────────────────────────
  if (cargando) return (
    <div className="d-flex justify-content-center align-items-center py-5">
      <div className="spinner-border text-primary me-3" role="status"></div>
      <span className="text-muted">Buscando órdenes pendientes de entrega...</span>
    </div>
  );

  if (error) return (
    <div className="alert alert-danger mt-3">
      <i className="bi bi-exclamation-triangle-fill me-2"></i>
      {error}
      <button className="btn btn-sm btn-outline-danger ms-3" onClick={() => cargar()}>
        Reintentar
      </button>
    </div>
  );

  const r = datos?.resumen || {};

  return (
    <div className="mt-3">
      <div className="d-flex justify-content-between align-items-start flex-wrap gap-2 mb-3">
        <div>
          <h5 className="mb-1">Pendiente de entrega</h5>
          <p className="text-muted small mb-0">
            Órdenes de compra aprobadas con líneas abiertas contra el almacén{' '}
            <code>{datos?.almacen}</code>: mercadería comprada que el proveedor
            todavía no entregó.
          </p>
        </div>
        <button className="btn btn-outline-success" onClick={descargarPlanilla}>
          <i className="bi bi-file-earmark-excel me-1"></i>
          Descargar planilla
        </button>
      </div>

      {r.ordenes === 0 ? (
        <div className="alert alert-success">
          <i className="bi bi-check-circle me-2"></i>
          No hay entregas pendientes: todas las órdenes de consignación están cerradas.
        </div>
      ) : (
        <>
          <div className="d-flex flex-wrap gap-2 mb-3">
            <span className="chip-resumen">
              <i className="bi bi-receipt"></i>
              {r.ordenes} orden(es) · {r.lineas} línea(s)
            </span>
            <span className="chip-resumen">
              <i className="bi bi-box"></i>
              {formatNumber(r.unidades)} unidades
            </span>
            <span className="chip-resumen">
              <i className="bi bi-cash-stack"></i>
              {formatMoney(r.importe)}
            </span>
            {r.vencidas > 0 && (
              <span className="chip-resumen text-danger">
                <i className="bi bi-exclamation-octagon"></i>
                {r.vencidas} vencida(s)
              </span>
            )}
            {r.sinFecha > 0 && (
              <span className="chip-resumen text-muted">
                <i className="bi bi-question-circle"></i>
                {r.sinFecha} sin fecha de entrega
              </span>
            )}
          </div>

          <div className="row g-2 mb-3">
            <div className="col-md-8">
              <div className="input-group input-group-sm">
                <span className="input-group-text"><i className="bi bi-search"></i></span>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Buscar por OC, proveedor, código o descripción..."
                  value={busqueda}
                  onChange={e => setBusqueda(e.target.value)}
                />
              </div>
            </div>
            <div className="col-md-4">
              <select
                className="form-select form-select-sm"
                value={filtroEstado}
                onChange={e => setFiltroEstado(e.target.value)}
              >
                <option value="TODOS">Todos los estados</option>
                <option value="vencido">Vencidas</option>
                <option value="porVencer">Por vencer</option>
                <option value="enFecha">En fecha</option>
                <option value="sinFecha">Sin fecha de entrega</option>
              </select>
            </div>
          </div>

          {ordenes.length === 0 ? (
            <div className="alert alert-light border">
              Ninguna orden coincide con el filtro.
            </div>
          ) : ordenes.map(oc => {
            const abierta = expandidas.includes(oc.docEntry);
            const estado = ESTADOS[oc.estado] || ESTADOS.sinFecha;
            return (
              <div className="card shadow-sm mb-2" key={oc.docEntry}>
                <div className="card-body py-3">
                  <div className="d-flex justify-content-between align-items-start flex-wrap gap-2">
                    <div style={{ minWidth: 0 }}>
                      <div className="d-flex align-items-center gap-2 flex-wrap">
                        <strong>OC #{oc.docNum}</strong>
                        <span className={`badge ${estado.clase}`}>
                          <i className={`bi ${estado.icono} me-1`}></i>
                          {estado.titulo}
                        </span>
                        <span className="text-muted small">{textoDias(oc.dias)}</span>
                      </div>
                      <div className="small mt-1">
                        {oc.cardName} <code className="text-muted">{oc.cardCode}</code>
                      </div>
                      <div className="text-muted small">
                        Emitida {formatFecha(oc.fecha)}
                        {oc.fechaEntrega && <> · entrega pactada {formatFecha(oc.fechaEntrega)}</>}
                        {oc.comprador && <> · {oc.comprador}</>}
                      </div>
                    </div>

                    <div className="text-end">
                      <div className="fw-bold">{formatMoney(oc.importePendiente)}</div>
                      <div className="text-muted small">
                        {formatNumber(oc.unidadesPendientes)} u. en {oc.lineas.length} línea(s)
                      </div>
                      <div className="mt-2 d-flex gap-2 justify-content-end">
                        <button
                          className="btn btn-sm btn-outline-secondary"
                          onClick={() => alternar(oc.docEntry)}
                        >
                          <i className={`bi ${abierta ? 'bi-chevron-up' : 'bi-chevron-down'} me-1`}></i>
                          {abierta ? 'Ocultar' : 'Ver'} detalle
                        </button>
                        <button
                          className="btn btn-sm btn-primary"
                          onClick={() => abrirReclamo(oc)}
                        >
                          <i className="bi bi-envelope me-1"></i>
                          Reclamar
                        </button>
                      </div>
                    </div>
                  </div>

                  {!oc.email && (
                    <div className="alert alert-warning py-2 px-3 mb-0 mt-2 small">
                      <i className="bi bi-exclamation-triangle me-1"></i>
                      El proveedor no tiene email cargado en SAP: hay que escribirlo a mano al reclamar.
                    </div>
                  )}

                  {abierta && (
                    <div className="table-responsive mt-3">
                      <table className="table table-sm align-middle mb-0">
                        <thead className="table-light">
                          <tr>
                            <th style={{ width: '110px' }}>Código</th>
                            <th>Descripción</th>
                            <th className="text-end">Pedido</th>
                            <th className="text-end">Recibido</th>
                            <th className="text-end">Pendiente</th>
                            <th className="text-end">Stock</th>
                            <th className="text-end">Importe</th>
                            <th className="text-nowrap">Entrega</th>
                          </tr>
                        </thead>
                        <tbody>
                          {oc.lineas.map(l => (
                            <tr key={`${oc.docEntry}-${l.linea}`}>
                              <td><code className="small">{l.codigo}</code></td>
                              <td className="small celda-descripcion" title={l.descripcion}>
                                {l.descripcion}
                              </td>
                              <td className="text-end">{formatNumber(l.pedido)}</td>
                              <td className="text-end">
                                {l.recibido > 0
                                  ? formatNumber(l.recibido)
                                  : <span className="text-muted">—</span>}
                              </td>
                              <td className="text-end fw-semibold">{formatNumber(l.pendiente)}</td>
                              <td className="text-end">
                                {/* Un artículo en quiebre esperando esta entrega
                                    es lo que hace urgente el reclamo. */}
                                {l.stock === null || l.stock === undefined
                                  ? <span className="text-muted">—</span>
                                  : l.enQuiebre
                                    ? <span className="semaforo semaforo-quiebre">
                                        <span className="punto" /> sin stock
                                      </span>
                                    : formatNumber(l.stock)}
                              </td>
                              <td className="text-end">{formatMoney(l.importePendiente)}</td>
                              <td className="small text-nowrap">
                                {l.fechaEntrega ? formatFecha(l.fechaEntrega) : '—'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </>
      )}

      {/* --- Modal de reclamo --- */}
      {ocReclamo && (
        <>
          <div className="modal fade show d-block" tabIndex="-1">
            <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
              <div className="modal-content">
                <div className="modal-header bg-primary text-white">
                  <h5 className="modal-title">
                    <i className="bi bi-envelope me-2"></i>
                    Reclamo de la OC #{ocReclamo.docNum}
                  </h5>
                  <button
                    type="button"
                    className="btn-close btn-close-white"
                    onClick={() => setOcReclamo(null)}
                    disabled={enviando}
                  ></button>
                </div>
                <div className="modal-body">
                  <div className="mb-3">
                    <label className="form-label small fw-semibold">Para</label>
                    <input
                      type="email"
                      className={`form-control ${!ocReclamo.email ? 'border-warning' : ''}`}
                      value={mailPara}
                      onChange={e => setMailPara(e.target.value)}
                      placeholder="correo@proveedor.com"
                    />
                    {!ocReclamo.email && (
                      <div className="form-text text-warning">
                        El proveedor no tiene email en SAP: completalo acá.
                      </div>
                    )}
                  </div>
                  <div className="mb-3">
                    <label className="form-label small fw-semibold">Asunto</label>
                    <input
                      type="text"
                      className="form-control"
                      value={mailAsunto}
                      onChange={e => setMailAsunto(e.target.value)}
                    />
                  </div>
                  <div className="mb-0">
                    <label className="form-label small fw-semibold">Mensaje</label>
                    <textarea
                      className="form-control font-monospace"
                      style={{ fontSize: '.85rem' }}
                      rows={14}
                      value={mailCuerpo}
                      onChange={e => setMailCuerpo(e.target.value)}
                    />
                  </div>
                </div>
                <div className="modal-footer">
                  <span className="text-muted small me-auto">
                    Sale desde la casilla de Compras. La respuesta le llega a quien reclama.
                  </span>
                  <button
                    className="btn btn-secondary"
                    onClick={() => setOcReclamo(null)}
                    disabled={enviando}
                  >
                    Cancelar
                  </button>
                  <button className="btn btn-primary" onClick={enviarReclamo} disabled={enviando}>
                    {enviando
                      ? <><span className="spinner-border spinner-border-sm me-2"></span>Enviando...</>
                      : <><i className="bi bi-send me-1"></i>Enviar reclamo</>}
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show"></div>
        </>
      )}
    </div>
  );
};

export default TabReclamos;
